import React, { useState, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Layers, Palette, Code, Sparkles, ArrowRight, Check, ChevronRight, Monitor, CodeXml, RotateCcw, Zap, Pencil, ExternalLink, X, LayoutDashboard } from 'lucide-react';
import DashboardShell from './dashboard/DashboardShell';
import WebContainerPreview from './build/WebContainerPreview';
import FileTreeExplorer from './build/FileTreeExplorer';
import { parseMultiFileOutput, hasMultiFileMarkers } from '../lib/webcontainer/parseMultiFileOutput';

const STAGE_DEFS = [
  { id: 'wireframe', labelKey: 'wireframe', subKey: 'wireframeSub', icon: Layers, color: '#7C3AED' },
  { id: 'design', labelKey: 'design', subKey: 'designSub', icon: Palette, color: '#7C3AED' },
  { id: 'code', labelKey: 'code', subKey: 'codeSub', icon: Code, color: '#7C3AED' },
];

const T = {
  en: {
    wireframe: 'Analysis', wireframeSub: 'Strategy & Architecture',
    design: 'Design', designSub: 'Style & Ambiance',
    code: 'Code', codeSub: 'Production Ready',
    pipeline: 'Creative Pipeline',
    previewCanvas: 'Preview Canvas',
    previewDesc: 'Describe your project idea in the chat panel. The AI will progressively generate a wireframe, design, and final code right here.',
    wireframeHeader: 'Analysis & Strategy', wireframeDesc: 'Vision, market, architecture',
    designHeader: 'Design System', designDesc: 'Palette, typography, visual style',
    inspirations: 'Inspirations & Components',
    waitWireframe: 'Waiting for your idea...', waitWireframeSub: 'Describe your project in the chat',
    waitDesign: 'Design in progress...', waitDesignSub: 'Aria is sketching the look & feel',
    waitCode: 'Aria is shipping your build...', waitCodeSub: 'The preview will appear here in a moment',
    build: 'Build', building: 'Building...',
  },
  fr: {
    wireframe: 'Analyse', wireframeSub: 'Strategie & Architecture',
    design: 'Design', designSub: 'Style & Ambiance',
    code: 'Code', codeSub: 'Production Ready',
    pipeline: 'Pipeline Creatif',
    previewCanvas: 'Preview Canvas',
    previewDesc: 'Decrivez votre projet dans le chat. L\'AI generera progressivement le wireframe, le design, puis le code final ici.',
    wireframeHeader: 'Analyse & Strategie', wireframeDesc: 'Vision, marche, architecture',
    designHeader: 'Design System', designDesc: 'Palette, typographie, style visuel',
    inspirations: 'Inspirations & Composants',
    waitWireframe: 'En attente de votre idee...', waitWireframeSub: 'Decrivez votre projet dans le chat',
    waitDesign: 'Design en preparation...', waitDesignSub: 'Aria dessine le look & feel',
    waitCode: 'Aria construit votre projet...', waitCodeSub: 'L\'aperçu va apparaître ici dans un instant',
    build: 'Build', building: 'Build en cours...',
  },
};

export default function PreviewPanel({ blocks, activeStage, setActiveStage, chatStarted, pipelineStage, onSelectTemplate, modifyMode, onSectionSelect, onNextStage, nextStageDisabled = false, lang = 'en', onClose, chatId = null }) {
  const t = T[lang] || T.en;
  const STAGES = STAGE_DEFS.map(s => ({ ...s, label: t[s.labelKey], sublabel: t[s.subKey] }));
  if (!chatStarted) {
    return <EmptyState t={t} stages={STAGES} />;
  }

  const wireframeBlocks = blocks.filter(b => b.stage === 'wireframe');
  const designBlocks = blocks.filter(b => b.stage === 'design');
  const codeBlocks = blocks.filter(b => b.type === 'code');
  const codeStageHasContent = blocks.some(b => b.stage === 'code');

  const stageContent = {
    wireframe: wireframeBlocks,
    design: designBlocks,
    code: codeStageHasContent ? [true] : codeBlocks,
  };

  const completedStages = STAGES.filter(s => stageContent[s.id].length > 0).map(s => s.id);

  return (
    <div data-testid="preview-panel" style={{
      flex: '1 1 62%',
      display: 'flex',
      flexDirection: 'column',
      background: 'var(--bg-depth-1)',
      overflow: 'hidden',
    }}>
      <div style={{
        flex: 1,
        overflowY: 'hidden',
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}>
        {codeBlocks.length > 0
          ? <CodeView blocks={codeBlocks} lang={lang} onClose={onClose} chatId={chatId} />
          : <StageLoading stage="code" t={t} />
        }
      </div>
    </div>
  );
}

function PipelineBar({ stages, activeStage, completedStages, onSelect, onNextStage, nextStageDisabled = false, pipelineStage, t }) {
  return (
    <div data-testid="pipeline-bar" style={{
      display: 'flex',
      alignItems: 'center',
      padding: '12px 20px',
      borderBottom: '1px solid var(--border-color)',
      background: 'var(--bg-depth-2)',
      flexShrink: 0,
      gap: 6,
    }}>
      {stages.map((stage, i) => {
        const isActive = activeStage === stage.id;
        const isCompleted = completedStages.includes(stage.id);
        const prevCompleted = i > 0 ? completedStages.includes(stages[i - 1]?.id) : true;
        const isAvailable = isCompleted || isActive || prevCompleted;
        const Icon = stage.icon;

        // Show "Next" button between the current active completed stage and the next one
        const prevStage = i > 0 ? stages[i - 1] : null;
        const prevIsActiveAndCompleted = prevStage && prevStage.id === pipelineStage && completedStages.includes(prevStage.id);
        const showNextBtn = i > 0 && prevIsActiveAndCompleted && !isCompleted && pipelineStage !== 'code';

        return (
          <React.Fragment key={stage.id}>
            {i > 0 && (
              showNextBtn ? (
                <button
                  data-testid="pipeline-next-btn"
                  onClick={nextStageDisabled ? undefined : onNextStage}
                  disabled={nextStageDisabled}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '5px 14px',
                    margin: '0 4px',
                    borderRadius: 0,
                    border: 'none',
                    background: nextStageDisabled ? 'rgba(124,58,237,0.25)' : '#7C3AED',
                    color: nextStageDisabled ? 'rgba(0,0,0,0.55)' : '#000',
                    fontSize: 10, fontWeight: 700,
                    fontFamily: 'inherit',
                    cursor: nextStageDisabled ? 'not-allowed' : 'pointer',
                    transition: 'all 0.25s ease',
                    letterSpacing: '0.03em',
                    textTransform: 'uppercase',
                  }}
                  onMouseEnter={e => { if (!nextStageDisabled) { e.currentTarget.style.boxShadow = '0 0 16px rgba(124,58,237,0.4)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  {nextStageDisabled ? (
                    <>
                      <span style={{
                        width: 9, height: 9, borderRadius: '50%',
                        border: '1.5px solid rgba(0,0,0,0.5)',
                        borderTopColor: 'transparent',
                        display: 'inline-block',
                        animation: 'spin 0.7s linear infinite',
                      }} />
                      Generating
                    </>
                  ) : (
                    <>
                      Next
                      <ArrowRight size={11} />
                    </>
                  )}
                </button>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 4px' }}>
                  <div style={{
                    width: 24, height: 1,
                    background: isCompleted || completedStages.includes(stages[i - 1]?.id)
                      ? 'rgba(124,58,237,0.4)' : 'var(--border-color)',
                    transition: 'background 0.3s',
                  }} />
                  <ChevronRight size={12} style={{
                    color: isCompleted ? 'rgba(124,58,237,0.5)' : 'var(--text-tertiary)',
                    opacity: 0.5,
                  }} />
                </div>
              )
            )}
            <button
              data-testid={`pipeline-stage-${stage.id}`}
              onClick={() => isAvailable && onSelect(stage.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 16px',
                borderRadius: 0,
                border: isActive ? `1px solid ${stage.color}30` : '1px solid transparent',
                background: isActive ? `${stage.color}12` : 'transparent',
                cursor: isAvailable ? 'pointer' : 'default',
                transition: 'all 0.2s',
                opacity: isAvailable ? 1 : 0.35,
                fontFamily: 'inherit',
              }}
            >
              <div style={{
                width: 28, height: 28,
                borderRadius: 0,
                background: isActive ? `${stage.color}20` : isCompleted ? `${stage.color}15` : 'var(--bg-depth-3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s',
              }}>
                {isCompleted && !isActive ? (
                  <Check size={13} style={{ color: stage.color }} />
                ) : (
                  <Icon size={13} style={{ color: isActive ? stage.color : 'var(--text-tertiary)' }} />
                )}
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: isActive ? stage.color : isCompleted ? 'var(--text-primary)' : 'var(--text-tertiary)',
                  lineHeight: 1.2,
                }}>
                  {stage.label}
                </div>
                <div style={{
                  fontSize: 10,
                  color: 'var(--text-tertiary)',
                  lineHeight: 1.2,
                  marginTop: 1,
                }}>
                  {stage.sublabel}
                </div>
              </div>
            </button>
          </React.Fragment>
        );
      })}

      <div style={{ flex: 1 }} />
    </div>
  );
}

function WireframeView({ content, t, modifyMode, onSectionSelect }) {
  const sections = useMemo(() => {
    const lines = content.split('\n');
    const result = [];
    let current = null;
    for (const line of lines) {
      if (/^##\s/.test(line) || /^###\s/.test(line)) {
        if (current) result.push(current);
        current = { heading: line.replace(/^#{2,3}\s*/, '').replace(/\*\*/g, '').trim(), content: '', level: line.startsWith('###') ? 3 : 2 };
      } else if (current) {
        current.content += line + '\n';
      }
    }
    if (current) result.push(current);
    return result.filter(s => s.level === 2 || (s.level === 3 && /\d/.test(s.heading)));
  }, [content]);

  return (
    <div data-testid="wireframe-view" style={{ animation: 'fadeIn 0.4s ease-out' }}>
      <div style={{
        marginBottom: 16,
        padding: '14px 18px',
        background: 'linear-gradient(135deg, rgba(124,58,237,0.08) 0%, rgba(124,58,237,0.02) 100%)',
        border: '1px solid rgba(124,58,237,0.1)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <Layers size={16} style={{ color: '#7C3AED' }} />
        <div>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{t.wireframeHeader}</span>
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginLeft: 8 }}>{t.wireframeDesc}</span>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {sections.map((sec, i) => (
          <WireframeCard key={i} section={sec} index={i} total={sections.length} modifyMode={modifyMode} onSectionSelect={onSectionSelect} />
        ))}
      </div>
    </div>
  );
}

function detectSectionType(heading) {
  const h = heading.toLowerCase().replace(/\d+\.\s*/, '');
  if (/reference.*analy|analyse.*ref|site.*analy/.test(h)) return 'reference';
  if (/vision|pitch|value.*prop|projet/.test(h)) return 'vision';
  if (/target|audience|persona|cible|march|market/.test(h)) return 'target';
  if (/business|model|revenue|monetiz|revenu|potentiel/.test(h)) return 'revenue';
  if (/competitive|concurren|edge|advantage|avantage|key.*feat|fonctionnalit/.test(h)) return 'edge';
  if (/architecture|site.*struct|page.*section|section/.test(h)) return 'architecture';
  if (/hero|banner|landing|splash|main.*section|accueil/.test(h)) return 'hero';
  if (/nav|header|menu|bar|navigation/.test(h)) return 'nav';
  if (/grid|portfolio|projects?|gallery|cards?|articles?|blog|work/.test(h)) return 'grid';
  if (/footer|bottom|copyright/.test(h)) return 'footer';
  if (/contact|form|newsletter|email|subscribe/.test(h)) return 'form';
  if (/about|team|who|intro|bio|story|overview/.test(h)) return 'about';
  if (/features?|services?|benefits?|capabilities|offer/.test(h)) return 'features';
  if (/testimonial|review|quote|feedback|client/.test(h)) return 'testimonials';
  if (/pricing|plan|package|tarif/.test(h)) return 'pricing';
  if (/stat|metric|number|counter|data|key.*fig|kpi/.test(h)) return 'stats';
  if (/cta|call.*action|conversion/.test(h)) return 'cta';
  return 'generic';
}

function WireframeSketch({ type }) {
  const box = (w, h, bg = 'rgba(0,0,0,0.06)', extra) => (
    <div style={{ width: w, height: h, background: bg, borderRadius: 0, flexShrink: 0, ...extra }} />
  );
  const line = (w, h = 3, bg = 'rgba(124,58,237,0.25)') => (
    <div style={{ width: w, height: h, background: bg, borderRadius: 1, flexShrink: 0 }} />
  );

  const wrap = { display: 'flex', flexDirection: 'column', gap: 4, width: '100%', padding: '12px 14px' };

  if (type === 'hero') return (
    <div style={{ ...wrap, flexDirection: 'row', alignItems: 'center', gap: 14 }}>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
        {line('85%', 5, 'rgba(124,58,237,0.3)')}
        {line('60%', 3)}
        {line('50%', 3, 'rgba(0,0,0,0.08)')}
        <div style={{ marginTop: 4 }}>{box(56, 16, 'rgba(124,58,237,0.2)')}</div>
      </div>
      {box(64, 48, 'rgba(0,0,0,0.04)', { border: '1px dashed rgba(0,0,0,0.08)' })}
    </div>
  );
  if (type === 'nav') return (
    <div style={{ ...wrap, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
      {box(28, 10, 'rgba(124,58,237,0.3)')}
      <div style={{ flex: 1 }} />
      {[1,2,3,4].map(i => <div key={i} style={{ width: 24, height: 3, background: 'rgba(0,0,0,0.02)' }} />)}
      {box(36, 12, 'rgba(124,58,237,0.15)')}
    </div>
  );
  if (type === 'grid') return (
    <div style={{ ...wrap }}>
      {line('40%', 4, 'rgba(124,58,237,0.2)')}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, marginTop: 4 }}>
        {[1,2,3].map(i => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {box('100%', 24, 'rgba(0,0,0,0.05)', { border: '1px solid rgba(0,0,0,0.06)' })}
            {line('70%', 2, 'rgba(0,0,0,0.0)')}
            {line('50%', 2, 'rgba(0,0,0,0.06)')}
          </div>
        ))}
      </div>
    </div>
  );
  if (type === 'footer') return (
    <div style={{ ...wrap }}>
      <div style={{ display: 'flex', gap: 16, marginBottom: 4 }}>
        {[1,2,3].map(i => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {line('60%', 3, 'rgba(124,58,237,0.15)')}
            {[1,2,3].map(j => <div key={j} style={{ width: '80%', height: 2, background: 'rgba(0,0,0,0.06)' }} />)}
          </div>
        ))}
      </div>
      <div style={{ height: 1, background: 'rgba(0,0,0,0.06)', margin: '2px 0' }} />
      {line('30%', 2, 'rgba(0,0,0,0.06)')}
    </div>
  );
  if (type === 'form') return (
    <div style={{ ...wrap, alignItems: 'center' }}>
      {line('50%', 4, 'rgba(124,58,237,0.2)')}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '70%', marginTop: 4 }}>
        {[1,2].map(i => <div key={i} style={{ height: 12, background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.08)' }} />)}
        <div style={{ height: 28, background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.08)' }} />
        {box(60, 14, 'rgba(124,58,237,0.2)', { alignSelf: 'flex-end' })}
      </div>
    </div>
  );
  if (type === 'about') return (
    <div style={{ ...wrap, flexDirection: 'row', gap: 12, alignItems: 'center' }}>
      {box(56, 56, 'rgba(0,0,0,0.04)', { border: '1px dashed rgba(0,0,0,0.08)', borderRadius: '50%' })}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {line('50%', 4, 'rgba(124,58,237,0.2)')}
        {line('90%', 2, 'rgba(0,0,0,0.08)')}
        {line('75%', 2, 'rgba(0,0,0,0.06)')}
        {line('85%', 2, 'rgba(0,0,0,0.06)')}
      </div>
    </div>
  );
  if (type === 'features') return (
    <div style={{ ...wrap }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
        {[1,2,3].map(i => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: 4 }}>
            {box(18, 18, 'rgba(124,58,237,0.12)')}
            {line('70%', 2, 'rgba(0,0,0,0.0)')}
            {line('90%', 2, 'rgba(0,0,0,0.05)')}
          </div>
        ))}
      </div>
    </div>
  );
  if (type === 'testimonials') return (
    <div style={{ ...wrap, flexDirection: 'row', gap: 6 }}>
      {[1,2].map(i => (
        <div key={i} style={{ flex: 1, padding: 6, background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
            {box(14, 14, 'rgba(124,58,237,0.1)', { borderRadius: '50%' })}
            {line('40%', 2, 'rgba(0,0,0,0.0)')}
          </div>
          {line('90%', 2, 'rgba(0,0,0,0.06)')}
          {line('70%', 2, 'rgba(0,0,0,0.04)')}
        </div>
      ))}
    </div>
  );
  if (type === 'stats') return (
    <div style={{ ...wrap, flexDirection: 'row', justifyContent: 'space-around' }}>
      {[1,2,3,4].map(i => (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          {line(20, 8, 'rgba(124,58,237,0.3)')}
          {line(30, 2, 'rgba(0,0,0,0.08)')}
        </div>
      ))}
    </div>
  );
  if (type === 'cta') return (
    <div style={{ ...wrap, alignItems: 'center', gap: 6 }}>
      {line('55%', 5, 'rgba(124,58,237,0.25)')}
      {line('35%', 3, 'rgba(0,0,0,0.08)')}
      {box(72, 18, 'rgba(124,58,237,0.2)')}
    </div>
  );
  if (type === 'pricing') return (
    <div style={{ ...wrap }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4 }}>
        {[1,2,3].map(i => (
          <div key={i} style={{ padding: 4, background: i === 2 ? 'rgba(124,58,237,0.05)' : 'rgba(0,0,0,0.02)', border: `1px solid ${i === 2 ? 'rgba(124,58,237,0.15)' : 'rgba(0,0,0,0.06)'}` }}>
            {line('50%', 3, 'rgba(124,58,237,0.15)')}
            {line(20, 6, 'rgba(0,0,0,0.02)')}
            {[1,2,3].map(j => <div key={j} style={{ width: '80%', height: 2, background: 'rgba(0,0,0,0.05)', marginTop: 2 }} />)}
          </div>
        ))}
      </div>
    </div>
  );
  // === BUSINESS / STRATEGY SECTIONS ===
  if (type === 'reference') return (
    <div style={{ ...wrap, flexDirection: 'row', gap: 10, alignItems: 'center' }}>
      <div style={{ width: 56, height: 40, background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(124,58,237,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <div style={{ width: 32, height: 24, background: 'rgba(124,58,237,0.08)', border: '1px dashed rgba(124,58,237,0.2)' }} />
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {line('40%', 3, 'rgba(124,58,237,0.3)')}
        {line('90%', 2, 'rgba(0,0,0,0.08)')}
        {line('70%', 2, 'rgba(0,0,0,0.06)')}
        <div style={{ display: 'flex', gap: 4, marginTop: 2 }}>
          {[1,2,3].map(i => <div key={i} style={{ width: 12, height: 12, background: `rgba(124,58,237,${0.08 + i * 0.04})`, borderRadius: 0, flexShrink: 0 }} />)}
        </div>
      </div>
    </div>
  );
  if (type === 'vision') return (
    <div style={{ ...wrap, alignItems: 'center', gap: 6 }}>
      <div style={{ width: 28, height: 28, background: 'rgba(124,58,237,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 12, height: 12, background: 'rgba(124,58,237,0.4)', borderRadius: '50%' }} />
      </div>
      {line('70%', 5, 'rgba(124,58,237,0.3)')}
      {line('50%', 3, 'rgba(0,0,0,0.0)')}
      <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
        {box(50, 14, 'rgba(124,58,237,0.12)')}
        {box(50, 14, 'rgba(0,0,0,0.04)', { border: '1px solid rgba(0,0,0,0.08)' })}
      </div>
    </div>
  );
  if (type === 'target') return (
    <div style={{ ...wrap, flexDirection: 'row', gap: 10, alignItems: 'center' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {[1,2,3].map(i => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 16, height: 16, borderRadius: '50%', background: `rgba(124,58,237,${0.08 + i * 0.06})`, border: '1px solid rgba(124,58,237,0.15)', flexShrink: 0 }} />
            {line(40, 2, 'rgba(0,0,0,0.08)')}
          </div>
        ))}
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {[28, 42, 56, 38, 50].map((h, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-end', gap: 3 }}>
            <div style={{ width: 14, height: h * 0.5, background: i === 2 ? 'rgba(124,58,237,0.3)' : `rgba(124,58,237,${0.06 + i * 0.03})` }} />
            {line(30, 2, 'rgba(0,0,0,0.05)')}
          </div>
        ))}
      </div>
    </div>
  );
  if (type === 'revenue') return (
    <div style={{ ...wrap, position: 'relative', height: 64 }}>
      <svg width="100%" height="50" viewBox="0 0 180 50" fill="none" style={{ overflow: 'visible' }}>
        <path d="M0 45 Q30 42 50 35 Q80 20 110 18 Q140 16 180 5" stroke="rgba(124,58,237,0.4)" strokeWidth="2" fill="none" />
        <path d="M0 45 Q30 42 50 35 Q80 20 110 18 Q140 16 180 5 V50 H0 Z" fill="rgba(124,58,237,0.06)" />
        {[0, 50, 110, 180].map((x, i) => <circle key={i} cx={x} cy={[45,35,18,5][i]} r="3" fill="#7C3AED" opacity={0.5 + i * 0.15} />)}
      </svg>
    </div>
  );
  if (type === 'edge') return (
    <div style={{ ...wrap, flexDirection: 'row', gap: 6, justifyContent: 'center' }}>
      {[1,2,3].map(i => (
        <div key={i} style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '6px 8px',
          background: i === 2 ? 'rgba(124,58,237,0.06)' : 'transparent',
          border: `1px solid ${i === 2 ? 'rgba(124,58,237,0.2)' : 'rgba(0,0,0,0.04)'}`,
        }}>
          <div style={{ width: 16, height: 16, background: `rgba(124,58,237,${0.1 + i * 0.08})`, transform: 'rotate(45deg)' }} />
          {line(26, 2, 'rgba(0,0,0,0.0)')}
        </div>
      ))}
    </div>
  );
  if (type === 'architecture') return (
    <div style={{ ...wrap }}>
      {line('35%', 3, 'rgba(124,58,237,0.2)')}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4, paddingLeft: 6, borderLeft: '2px solid rgba(124,58,237,0.15)' }}>
        {['85%','75%','80%','70%','65%'].map((w, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {box(6, 6, 'rgba(124,58,237,0.2)')}
            <div style={{ width: w, height: 2, background: 'rgba(0,0,0,0.07)' }} />
          </div>
        ))}
      </div>
    </div>
  );
  // layout / meta / generic
  return (
    <div style={{ ...wrap }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {line('60%', 3, 'rgba(124,58,237,0.15)')}
          {line('90%', 2, 'rgba(0,0,0,0.06)')}
          {line('75%', 2, 'rgba(0,0,0,0.05)')}
        </div>
        {box(40, 28, 'rgba(0,0,0,0.03)', { border: '1px dashed rgba(0,0,0,0.06)' })}
      </div>
    </div>
  );
}

// Section icons and config
const SECTION_ICONS = {
  reference: Monitor, vision: Zap, target: Sparkles, revenue: Zap, edge: Sparkles,
  architecture: Layers, features: Layers, hero: Zap, nav: Monitor, grid: Layers,
  footer: Code, form: Sparkles, about: Sparkles, testimonials: Sparkles,
  pricing: Layers, stats: Zap, cta: Zap, generic: ArrowRight,
};

// Section priority/size config
const SECTION_PRIORITY = {
  reference: 'hero', vision: 'hero', revenue: 'featured',
  target: 'featured', edge: 'featured',
  architecture: 'normal', features: 'normal',
  hero: 'normal', nav: 'compact', grid: 'normal', footer: 'compact',
  form: 'compact', about: 'normal', testimonials: 'normal', pricing: 'normal',
  stats: 'featured', cta: 'compact', generic: 'normal',
};

const SECTION_COLORS = {
  reference: '#7C3AED', vision: '#7C3AED', revenue: '#00E5A0',
  target: '#00D4FF', edge: '#FFD700', architecture: '#9B8FFF',
  features: '#7C3AED', hero: '#7C3AED', generic: '#7C3AED',
};

// Extract key metrics/numbers from content
function extractMetrics(content) {
  const metrics = [];
  const lines = content.split('\n');
  for (const line of lines) {
    // Match dollar amounts, percentages, numbers with context
    const moneyMatch = line.match(/\$[\d,]+[KkMm]?(?:\/mo(?:nth)?)?|[\d,]+\s*(?:USD|EUR)/);
    const pctMatch = line.match(/\d+(?:\.\d+)?%/);
    const bigNumMatch = line.match(/\b(\d{1,3}(?:,\d{3})+|\d+[KkMmBb])\b/);
    if (moneyMatch) metrics.push({ value: moneyMatch[0], label: line.replace(/^[\s\-*]+/, '').replace(moneyMatch[0], '').replace(/[:\-–]/g, '').trim().slice(0, 30) });
    else if (pctMatch && metrics.length < 4) metrics.push({ value: pctMatch[0], label: line.replace(/^[\s\-*]+/, '').replace(pctMatch[0], '').replace(/[:\-–]/g, '').trim().slice(0, 30) });
    else if (bigNumMatch && metrics.length < 4) metrics.push({ value: bigNumMatch[0], label: line.replace(/^[\s\-*]+/, '').replace(bigNumMatch[0], '').replace(/[:\-–]/g, '').trim().slice(0, 30) });
    if (metrics.length >= 4) break;
  }
  return metrics;
}

// Extract bold items as key highlights
function extractHighlights(content, max = 4) {
  const highlights = [];
  const boldRegex = /\*\*([^*]+)\*\*/g;
  let m;
  while ((m = boldRegex.exec(content)) !== null && highlights.length < max) {
    const text = m[1].trim();
    if (text.length > 3 && text.length < 60) highlights.push(text);
  }
  return highlights;
}

function WireframeCard({ section, index, total, modifyMode, onSectionSelect }) {
  const type = detectSectionType(section.heading);
  const Icon = SECTION_ICONS[type] || ArrowRight;
  const isClickable = modifyMode && onSectionSelect;
  const priority = SECTION_PRIORITY[type] || 'normal';
  const accentColor = SECTION_COLORS[type] || '#7C3AED';

  const bullets = section.content
    .split('\n')
    .filter(l => l.trim().startsWith('-') || l.trim().startsWith('*'))
    .map(l => l.replace(/^[\s\-*]+/, '').replace(/\*\*/g, '').trim())
    .filter(l => l.length > 3)
    .slice(0, priority === 'hero' ? 5 : 3);

  const metrics = extractMetrics(section.content);
  const highlights = extractHighlights(section.content);

  const isHero = priority === 'hero';
  const isFeatured = priority === 'featured';
  const isCompact = priority === 'compact';

  return (
    <div
      data-testid={`wireframe-section-${index}`}
      onClick={isClickable ? () => onSectionSelect(section.heading) : undefined}
      style={{
        border: isHero ? `1px solid ${accentColor}30` : '1px solid var(--border-color)',
        background: isHero ? `linear-gradient(135deg, ${accentColor}08, transparent 70%)` : 'var(--bg-depth-2)',
        overflow: 'hidden',
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        position: 'relative',
        animation: `fadeIn 0.4s ease-out ${index * 0.06}s both`,
      }}
      onMouseEnter={e => {
        if (isClickable) {
          e.currentTarget.style.borderColor = accentColor;
          e.currentTarget.style.background = `${accentColor}08`;
        }
      }}
      onMouseLeave={e => {
        if (isClickable) {
          e.currentTarget.style.borderColor = isHero ? `${accentColor}30` : 'var(--border-color)';
          e.currentTarget.style.background = isHero ? `linear-gradient(135deg, ${accentColor}08, transparent 70%)` : 'var(--bg-depth-2)';
        }
      }}
    >
      {/* Accent bar */}
      <div style={{
        position: 'absolute', left: 0, top: 0, bottom: 0, width: isHero ? 4 : 3,
        background: accentColor,
        opacity: isHero ? 0.6 : 0.2,
      }} />

      <div style={{ padding: isHero ? '16px 18px 16px 22px' : isCompact ? '10px 14px 10px 18px' : '12px 16px 12px 20px' }}>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: isHero ? 12 : isCompact ? 4 : 8 }}>
          <div style={{
            width: isHero ? 28 : 22, height: isHero ? 28 : 22,
            background: `${accentColor}18`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Icon size={isHero ? 14 : 11} style={{ color: accentColor }} />
          </div>
          <span style={{
            fontSize: isHero ? 15 : 13, fontWeight: 700,
            color: isHero ? accentColor : 'var(--text-primary)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            flex: 1,
          }}>
            {section.heading.replace(/^\d+\.\s*/, '')}
          </span>
          {isClickable && <Pencil size={11} style={{ color: accentColor, opacity: 0.5, flexShrink: 0 }} />}
        </div>

        {/* Metrics row — for sections with numbers */}
        {metrics.length > 0 && !isCompact && (
          <div style={{
            display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap',
          }}>
            {metrics.slice(0, isFeatured || isHero ? 4 : 2).map((m, i) => (
              <div key={i} style={{
                padding: '6px 10px',
                background: `${accentColor}08`,
                border: `1px solid ${accentColor}15`,
                minWidth: 0, flex: '1 1 auto',
              }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: accentColor, lineHeight: 1.2 }}>
                  {m.value}
                </div>
                <div style={{ fontSize: 9, color: 'var(--text-tertiary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.label || 'metric'}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Highlights — for sections with bold items (competitors, USPs) */}
        {metrics.length === 0 && highlights.length > 0 && !isCompact && (
          <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
            {highlights.slice(0, 3).map((h, i) => (
              <span key={i} style={{
                fontSize: 10, fontWeight: 600,
                padding: '2px 8px',
                background: `${accentColor}10`,
                color: accentColor,
                border: `1px solid ${accentColor}15`,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160,
              }}>
                {h}
              </span>
            ))}
          </div>
        )}

        {/* Bullets */}
        {!isCompact && bullets.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: isHero ? 4 : 2 }}>
            {bullets.map((b, i) => (
              <div key={i} style={{
                fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.5,
                paddingLeft: 8,
                borderLeft: i === 0 ? `2px solid ${accentColor}30` : '2px solid rgba(0,0,0,0.04)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {b}
              </div>
            ))}
          </div>
        )}

        {/* Compact: single line summary */}
        {isCompact && bullets.length > 0 && (
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {bullets[0]}
          </div>
        )}
      </div>
    </div>
  );
}

function DesignView({ content, onSelectTemplate, t, modifyMode, onSectionSelect }) {
  return (
    <div data-testid="design-view" style={{ animation: 'fadeIn 0.4s ease-out' }}>
      <div style={{
        marginBottom: 20,
        padding: '18px 22px',
        background: 'linear-gradient(135deg, rgba(124,58,237,0.1) 0%, rgba(124,58,237,0.03) 100%)',
        borderRadius: 0,
        border: '1px solid rgba(124,58,237,0.12)',
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 0,
          background: 'rgba(124,58,237,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Palette size={18} style={{ color: '#7C3AED' }} />
        </div>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
            {t.designHeader}
          </h2>
          <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: 0, marginTop: 2 }}>
            {t.designDesc}
          </p>
        </div>
      </div>
      <MarkdownRenderer content={content} accentColor="#7C3AED" modifyMode={modifyMode} onSectionSelect={onSectionSelect} />

      <div style={{ marginTop: 28 }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
        }}>
          <Sparkles size={15} style={{ color: '#7C3AED' }} />
          <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
            {t.inspirations}
          </h3>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 12,
        }}>
          {INSPIRATION_CARDS.map((card, i) => (
            <InspirationCard key={i} card={card} index={i} onSelect={onSelectTemplate} />
          ))}
        </div>
      </div>
    </div>
  );
}

const INSPIRATION_CARDS = [
  {
    name: 'Scroll Media Hero',
    registryId: 'arunachalam0606/scroll-expansion-hero',
    tags: ['Hero', 'Scroll', 'Animation'],
    preview: 'https://cdn.21st.dev/cdn-cgi/image/fit=scale-down,width=600,quality=75,format=auto/https://cdn.21st.dev/user_2wfnj4jcSPR8JWRk7tpzrihdDV8/scroll-expansion-hero/default/preview.1746632883284.png',
    downloads: 541,
  },
  {
    name: 'Video Scroll Hero',
    registryId: 'isaiahbjork/video-scroll-hero',
    tags: ['Video', 'Parallax', 'Hero'],
    preview: 'https://cdn.21st.dev/cdn-cgi/image/fit=scale-down,width=600,quality=75,format=auto/https://cdn.21st.dev/isaiahbjork/video-scroll-hero/default/preview.1757969807899.png',
    downloads: 350,
  },
  {
    name: 'AI Prompt Box',
    registryId: 'easemize/ai-prompt-box',
    tags: ['AI', 'Input', 'Chat'],
    preview: 'https://cdn.21st.dev/cdn-cgi/image/fit=scale-down,width=600,quality=75,format=auto/https://cdn.21st.dev/user_2xJxXqdwWohYrdd6fiMYrS9hbmO/ai-prompt-box/default/preview.1748459134010.png',
    downloads: 275,
  },
  {
    name: 'V0 AI Chat',
    registryId: 'kokonutd/v0-ai-chat',
    tags: ['AI', 'Chat', 'Interface'],
    preview: 'https://cdn.21st.dev/cdn-cgi/image/fit=scale-down,width=600,quality=75,format=auto/https://cdn.21st.dev/user_2rQ1QHrJyxpmWMHhqhANzWMc64n/v0-ai-chat/default/preview.png',
    downloads: 265,
  },
  {
    name: 'Hero Section',
    registryId: 'tailark/hero-section-5',
    tags: ['Hero', 'Landing', 'Modern'],
    preview: 'https://cdn.21st.dev/cdn-cgi/image/fit=scale-down,width=600,quality=75,format=auto/https://cdn.21st.dev/user_2tUYFzCDCVfrMrVC3TfB9DUBAyx/hero-section-5/default/preview.png',
    downloads: 205,
  },
  {
    name: 'Hero Video Dialog',
    registryId: 'magicui/hero-video-dialog',
    tags: ['Video', 'Modal', 'Hero'],
    preview: 'https://cdn.21st.dev/cdn-cgi/image/fit=scale-down,width=600,quality=75,format=auto/https://cdn.21st.dev/user_magicui/hero-video-dialog/default/preview.png',
    downloads: 167,
  },
  {
    name: 'Video Player',
    registryId: 'chetanverma16/video-player',
    tags: ['Video', 'Player', 'Controls'],
    preview: 'https://cdn.21st.dev/cdn-cgi/image/fit=scale-down,width=600,quality=75,format=auto/https://cdn.21st.dev/user_2rc5PfL1BTeoSaeKi50f3UeVfRn/video-player/default/preview.1737980095562.png',
    downloads: 193,
  },
  {
    name: 'HoverPlay Card',
    registryId: 'ruixenui/hover-play-card',
    tags: ['Card', 'Hover', 'Video'],
    preview: 'https://cdn.21st.dev/cdn-cgi/image/fit=scale-down,width=600,quality=75,format=auto/https://cdn.21st.dev/ruixen.ui/hover-play-card/default/preview.1757867496156.png',
    downloads: 107,
  },
  {
    name: 'Onboarding Checklist',
    registryId: 'ravikatiyar162/onboarding-checklist',
    tags: ['Onboarding', 'Checklist', 'UI'],
    preview: 'https://cdn.21st.dev/cdn-cgi/image/fit=scale-down,width=600,quality=75,format=auto/https://cdn.21st.dev/ravikatiyar162/onboarding-checklist/default/preview.1759516044686.png',
    downloads: 153,
  },
  {
    name: 'Dynamic Frame Layout',
    registryId: 'oeneco/dynamic-frame-layout',
    tags: ['Layout', 'Grid', 'Dynamic'],
    preview: 'https://cdn.21st.dev/cdn-cgi/image/fit=scale-down,width=600,quality=75,format=auto/https://cdn.21st.dev/user_oeneco/dynamic-frame-layout/default/preview.png',
    downloads: 175,
  },
  {
    name: 'Animated AI Chat',
    registryId: 'jatin-yadav05/animated-ai-chat',
    tags: ['AI', 'Chat', 'Animated'],
    preview: 'https://cdn.21st.dev/cdn-cgi/image/fit=scale-down,width=600,quality=75,format=auto/https://cdn.21st.dev/user_2ra80vx0iKMyQWJ5AESmNfFbCHD/animated-ai-chat/default/preview.1746632407641.png',
    downloads: 236,
  },
  {
    name: 'Shine Border',
    registryId: 'magicui/shine-border',
    tags: ['Border', 'Glow', 'Effect'],
    preview: 'https://cdn.21st.dev/cdn-cgi/image/fit=scale-down,width=600,quality=75,format=auto/https://cdn.21st.dev/user_magicui/shine-border/824/preview.png',
    downloads: 73,
  },
];

function InspirationCard({ card, index, onSelect }) {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (loading || !onSelect) return;
    setLoading(true);
    try {
      await onSelect(card);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid={`inspiration-card-${index}`}
      onClick={handleClick}
      style={{
        borderRadius: 0,
        overflow: 'hidden',
        border: loading ? '1px solid var(--accent)' : '1px solid var(--border-color)',
        background: 'var(--bg-depth-2)',
        cursor: loading ? 'wait' : 'pointer',
        transition: 'all 0.25s ease',
        animation: `fadeIn 0.4s ease-out ${index * 0.05}s both`,
        opacity: loading ? 0.7 : 1,
        position: 'relative',
      }}
      onMouseEnter={e => {
        if (!loading) {
          e.currentTarget.style.borderColor = 'var(--accent)';
          e.currentTarget.style.transform = 'translateY(-3px)';
          e.currentTarget.style.boxShadow = '0 10px 40px rgba(124,58,237,0.2)';
        }
      }}
      onMouseLeave={e => {
        if (!loading) {
          e.currentTarget.style.borderColor = 'var(--border-color)';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
    >
      {loading && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 5,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            width: 24, height: 24,
            border: '2px solid var(--accent)',
            borderTopColor: 'transparent', borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }} />
        </div>
      )}
      <div style={{
        height: 130, overflow: 'hidden',
        position: 'relative', background: '#F0F0F4',
      }}>
        <img
          src={card.preview} alt={card.name}
          style={{
            width: '100%', height: '100%', objectFit: 'cover',
            transition: 'transform 0.3s ease',
          }}
          loading="lazy"
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 40%)',
        }} />
        <div style={{
          position: 'absolute', top: 8, right: 8,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)',
          borderRadius: 0, padding: '2px 8px',
          fontSize: 10, color: '#000', fontWeight: 600,
          display: 'flex', alignItems: 'center', gap: 3,
        }}>
          <ArrowRight size={8} style={{ transform: 'rotate(-45deg)' }} />
          {card.downloads}
        </div>
      </div>
      <div style={{ padding: '10px 12px' }}>
        <div style={{
          fontSize: 13, fontWeight: 600,
          color: 'var(--text-primary)', marginBottom: 6,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {card.name}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {card.tags.map(tag => (
            <span key={tag} style={{
              fontSize: 10, fontWeight: 500,
              background: 'rgba(124,58,237,0.1)', color: '#7C3AED',
              padding: '2px 7px', borderRadius: 0,
            }}>
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}


function CodeView({ blocks, lang = 'en', onClose, chatId = null }) {
  const [viewMode, setViewMode] = useState('preview');
  const iframeRef = useRef(null);

  const htmlLang = lang === 'fr' ? 'fr' : 'en';

  // === Multi-file (WebContainer) detection ===
  // If Claude produced a structured multi-file project, route it to the
  // WebContainer viewer (Vite/Next/Express runtime). Falls back silently to
  // the legacy single-file iframe srcDoc path if no multi-file markers are
  // present OR the parser fails.
  const rawCombined = useMemo(
    () => blocks.map(b => b.content || '').join('\n\n'),
    [blocks],
  );
  const fileSystemTree = useMemo(() => {
    if (!rawCombined) return null;
    if (!hasMultiFileMarkers(rawCombined)) return null;
    try {
      return parseMultiFileOutput(rawCombined);
    } catch (e) {
      console.warn('[PreviewPanel] multi-file parse failed:', e);
      return null;
    }
  }, [rawCombined]);

  const allCode = blocks.map(b => ({ lang: (b.language || '').toLowerCase(), content: b.content }));
  const jsxBlock = allCode.find(b => ['jsx', 'tsx', 'react', 'javascript', 'js'].includes(b.lang));
  const htmlBlock = allCode.find(b => ['html', 'htm'].includes(b.lang));
  const cssBlock = allCode.find(b => ['css', 'scss', 'sass'].includes(b.lang));

  // eslint-disable-next-line no-useless-concat
  const scriptEnd = '</scr' + 'ipt>';

  // Script to keep all navigation inside the iframe
  const navGuardScript = `<script>document.addEventListener('click',function(e){var a=e.target.closest('a');if(!a)return;var h=a.getAttribute('href');if(!h||h==='#')return e.preventDefault();if(h.startsWith('#')){e.preventDefault();var t=document.querySelector(h);if(t)t.scrollIntoView({behavior:'smooth'});return}if(h.startsWith('http')||h.startsWith('//')){e.preventDefault();return}e.preventDefault()},true);${scriptEnd}`;

  const iframeDoc = useMemo(() => {
    let htmlContent = '';
    let cssContent = cssBlock?.content || '';

    if (htmlBlock) htmlContent = htmlBlock.content;

    if (jsxBlock) {
      const code = jsxBlock.content;
      const isComponent = code.includes('import ') || code.includes('const ') || code.includes('function ') || code.includes('=>');
      if (isComponent) {
        return `<!DOCTYPE html><html lang="${htmlLang}"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1.0">
<script src="https://cdn.jsdelivr.net/npm/react@18/umd/react.production.min.js" crossorigin>${scriptEnd}
<script src="https://cdn.jsdelivr.net/npm/react-dom@18/umd/react-dom.production.min.js" crossorigin>${scriptEnd}
<script src="https://cdn.jsdelivr.net/npm/@babel/standalone/babel.min.js">${scriptEnd}
<script src="https://unpkg.com/lucide@latest/dist/umd/lucide.min.js">${scriptEnd}
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet">
<link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800;900&display=swap" rel="stylesheet">
<link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet">
<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css">
<script src="https://cdn.tailwindcss.com">${scriptEnd}
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Inter',sans-serif}img{max-width:100%}${cssContent}</style>
<script>
// Lucide icon React components via CDN — renders real SVG icons
(function(){
  function toKebab(s){return s.replace(/([A-Z])/g,function(m,c,i){return(i?'-':'')+c.toLowerCase()})}
  function makeIcon(name){
    return function(props){
      var p=props||{},sz=p.size||24,co=p.color||p.stroke||'currentColor',cn=p.className||'';
      var kb=toKebab(name);
      // Try to get real Lucide icon SVG data
      if(window.lucide&&window.lucide.icons&&window.lucide.icons[kb]){
        var d=window.lucide.icons[kb];
        var attrs=d[0]||{};
        var els=(d[1]||[]).map(function(el,i){
          var tag=el[0],a=Object.assign({},el[1]||{},{key:''+i});
          return React.createElement(tag,a);
        });
        return React.createElement('svg',Object.assign({},{xmlns:'http://www.w3.org/2000/svg',width:sz,height:sz,viewBox:'0 0 24 24',fill:'none',stroke:co,strokeWidth:attrs['stroke-width']||2,strokeLinecap:'round',strokeLinejoin:'round',className:cn,style:p.style},attrs.class?{}:{}),els);
      }
      // Fallback: render as data-lucide for createIcons to pick up
      return React.createElement('i',{'data-lucide':kb,style:Object.assign({display:'inline-block',width:sz,height:sz},p.style||{}),className:cn});
    };
  }
  var icons=['ChevronDown','ChevronUp','ChevronLeft','ChevronRight','Menu','X','Search','ArrowRight','ArrowLeft','ArrowUp','ArrowDown','Heart','Star','Mail','Phone','MapPin','Calendar','Clock','User','Users','Settings','Home','Globe','Link','ExternalLink','Eye','EyeOff','Check','Plus','Minus','Edit','Trash','Download','Upload','Share','Copy','Filter','Sun','Moon','Github','Twitter','Linkedin','Instagram','Facebook','Youtube','Play','Pause','Volume2','Camera','Image','Code','Terminal','Zap','Sparkles','Award','Shield','Lock','Unlock','Send','MessageCircle','Bell','Bookmark','Tag','Folder','File','Grid','List','Layout','Monitor','Smartphone','Tablet','Cpu','Database','Cloud','Wifi','Battery','Activity','TrendingUp','BarChart','PieChart','Target','Crosshair','Navigation','Compass','Map','Flag','Gift','Coffee','Briefcase','Building','Rocket','Flame','Move','MoreHorizontal','MoreVertical','Info','AlertCircle','CheckCircle','XCircle','Loader','RefreshCw','RotateCcw','Maximize','Minimize','Circle','Square','Triangle','Hexagon','MousePointer'];
  icons.forEach(function(n){window[n]=makeIcon(n)});
  // Catch-all proxy for any icon not in the list
  window.__iconFallback=new Proxy({},{get:function(_,n){if(n==='__esModule')return false;return makeIcon(n)}});
  // framer-motion shims — render as regular HTML elements
  window.motion=new Proxy({},{get:function(_,tag){
    if(tag==='__esModule')return false;
    return React.forwardRef(function(props,ref){
      var p=Object.assign({},props);
      delete p.initial;delete p.animate;delete p.exit;delete p.transition;delete p.whileHover;delete p.whileInView;delete p.whileTap;delete p.variants;delete p.viewport;
      p.ref=ref;
      return React.createElement(tag,p);
    });
  }});
  window.AnimatePresence=function(p){return p.children};
  window.useInView=function(){return[React.useRef(),true]};
  window.useAnimation=function(){return{start:function(){},set:function(){}}};
  // After React renders, activate Lucide icons on any remaining <i data-lucide> elements
  var mo=new MutationObserver(function(){if(window.lucide&&window.lucide.createIcons)try{window.lucide.createIcons()}catch(e){}});
  document.addEventListener('DOMContentLoaded',function(){
    mo.observe(document.body,{childList:true,subtree:true});
    setTimeout(function(){if(window.lucide&&window.lucide.createIcons)try{window.lucide.createIcons()}catch(e){}},300);
  });
})();
${scriptEnd}
</head>
<body><div id="root"></div><script type="text/babel">
${cleanImports(code)}
var AppComponent = typeof App !== 'undefined' ? App : typeof ${extractComponentName(code)} !== 'undefined' ? ${extractComponentName(code)} : function(){return React.createElement('div',null,'Loading...')};
ReactDOM.createRoot(document.getElementById('root')).render(React.createElement(AppComponent));
${scriptEnd}</body></html>`;
      }
    }

    if (htmlBlock) {
      if (htmlContent.includes('<html') || htmlContent.includes('<!DOCTYPE')) {
        // Inject essential CDNs if missing
        if (!htmlContent.includes('tailwindcss')) {
          htmlContent = htmlContent.replace('</head>', `<script src="https://cdn.tailwindcss.com">${scriptEnd}\n</head>`);
        }
        if (!htmlContent.includes('font-awesome') && !htmlContent.includes('fontawesome')) {
          htmlContent = htmlContent.replace('</head>', `<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css">\n</head>`);
        }
        return htmlContent;
      }
      return `<!DOCTYPE html><html lang="${htmlLang}"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1.0">
<link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet">
<link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800;900&display=swap" rel="stylesheet">
<script src="https://cdn.tailwindcss.com">${scriptEnd}
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Inter',sans-serif}${cssContent}</style></head>
<body>${htmlContent}</body></html>`;
    }

    const combinedCode = allCode.map(b => b.content).join('\n\n');
    return `<!DOCTYPE html><html lang="${htmlLang}"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1.0">
<script src="https://cdn.tailwindcss.com">${scriptEnd}
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Inter',sans-serif;padding:20px}${cssContent}</style></head>
<body><pre style="white-space:pre-wrap;font-family:monospace;font-size:13px;padding:20px;background:#1e1e2e;color:#cdd6f4;border-radius:12px">${escapeHtml(combinedCode)}</pre></body></html>`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks, htmlLang]);

  // Inject navigation guard into final HTML
  const safeSrcDoc = useMemo(() => {
    if (!iframeDoc) return '';
    // Insert nav guard script before </body>
    if (iframeDoc.includes('</body>')) {
      return iframeDoc.replace('</body>', navGuardScript + '</body>');
    }
    return iframeDoc + navGuardScript;
  }, [iframeDoc, navGuardScript]);

  const handleRefresh = () => {
    if (iframeRef.current) {
      iframeRef.current.srcdoc = '';
      setTimeout(() => { if (iframeRef.current) iframeRef.current.srcdoc = safeSrcDoc; }, 50);
    }
  };

  // Open the live preview in a standalone tab.
  const handleOpenInTab = () => {
    try {
      const blob = new Blob([safeSrcDoc || ''], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      // Revoke after a delay to free memory while still letting the tab load it
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      console.warn('[CodeView] open-in-tab failed:', e);
    }
  };

  // Shared icon-button style for the preview toolbar (right side)
  const tbBtn = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28,
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.08)',
    color: 'rgba(255,255,255,0.6)',
    cursor: 'pointer',
    transition: 'all 0.15s',
    fontFamily: 'inherit',
  };
  const tbSegmentBtn = (active) => ({
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '6px 12px',
    background: active ? 'rgba(124,58,237,0.12)' : 'transparent',
    border: 'none',
    color: active ? '#7C3AED' : 'rgba(255,255,255,0.55)',
    fontSize: 11,
    fontWeight: active ? 700 : 600,
    letterSpacing: '0.03em',
    cursor: 'pointer',
    transition: 'all 0.15s',
    fontFamily: 'inherit',
  });

  return (
    <div data-testid="code-view" style={{
      animation: 'fadeIn 0.4s ease-out',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
    }}>
      {/* === Preview Toolbar (header) === */}
      <div data-testid="preview-toolbar" style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '0 12px',
        background: '#0A0A10',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        flexShrink: 0,
        height: 44,
      }}>
        <span style={{
          fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
          textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)',
        }}>
          App Preview
        </span>

        <span style={{ flex: 1 }} />

        {/* Preview / Code / Dashboard segmented control */}
        <div style={{
          display: 'flex', gap: 0,
          border: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(255,255,255,0.02)',
        }}>
          <button
            data-testid="float-btn-preview"
            onClick={() => setViewMode('preview')}
            style={tbSegmentBtn(viewMode === 'preview')}
          >
            <Monitor size={12} /> Preview
          </button>
          <button
            data-testid="float-btn-code"
            onClick={() => setViewMode('code')}
            style={tbSegmentBtn(viewMode === 'code')}
          >
            <CodeXml size={12} /> Code
          </button>
          <button
            data-testid="float-btn-dashboard"
            onClick={() => setViewMode('dashboard')}
            style={tbSegmentBtn(viewMode === 'dashboard')}
          >
            <LayoutDashboard size={12} /> Dashboard
          </button>
        </div>

        {/* Open in new tab */}
        <button
          data-testid="open-preview-tab-btn"
          onClick={handleOpenInTab}
          title="Open in new tab"
          style={tbBtn}
          onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
        >
          <ExternalLink size={13} />
        </button>

        {/* Refresh */}
        <button
          data-testid="refresh-preview-toolbar"
          onClick={handleRefresh}
          title="Refresh preview"
          style={tbBtn}
          onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
        >
          <RotateCcw size={13} />
        </button>

        {/* Close */}
        {onClose && (
          <button
            data-testid="close-preview-btn"
            onClick={onClose}
            title="Close project"
            style={tbBtn}
            onMouseEnter={e => { e.currentTarget.style.color = '#FF5F57'; e.currentTarget.style.borderColor = 'rgba(255,95,87,0.4)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
          >
            <X size={13} />
          </button>
        )}
      </div>

      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        padding: '14px 18px',
        minHeight: 0,
      }}>
        {viewMode === 'preview' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {fileSystemTree ? (
              // Multi-file project detected → boot WebContainer with full Node.js
              // runtime (npm install + Vite/Next/Express). Falls back gracefully
              // on non-Chromium browsers via the component's own UI.
              <div style={{
                flex: 1,
                borderRadius: 0,
                overflow: 'hidden',
                border: '1px solid var(--border-color)',
                display: 'flex', flexDirection: 'column',
                minHeight: 0,
              }}>
                <WebContainerPreview files={fileSystemTree} height="100%" />
              </div>
            ) : (
              <div style={{
              flex: 1,
              borderRadius: 0,
              overflow: 'hidden',
              border: '1px solid var(--border-color)',
              background: '#fff',
              display: 'flex', flexDirection: 'column',
              minHeight: 0,
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 12px',
                background: 'var(--bg-depth-2)',
                borderBottom: '1px solid var(--border-color)',
                flexShrink: 0,
              }}>
                <div style={{ display: 'flex', gap: 5 }}>
                  <div style={{ width: 9, height: 9, borderRadius: 0, background: '#ff5f57' }} />
                  <div style={{ width: 9, height: 9, borderRadius: 0, background: '#febc2e' }} />
                  <div style={{ width: 9, height: 9, borderRadius: 0, background: '#28c840' }} />
                </div>
                <div style={{
                  flex: 1, background: 'var(--bg-depth-3)', borderRadius: 0,
                  padding: '3px 10px', fontSize: 10, color: 'var(--text-tertiary)',
                  fontFamily: "'JetBrains Mono', monospace",
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <span style={{ opacity: 0.5 }}>~</span>
                  localhost:3000
                </div>
                <button onClick={handleRefresh} title="Refresh" data-testid="refresh-preview" style={{
                  background: 'none', border: 'none', padding: 2,
                  color: 'var(--text-tertiary)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center',
                  opacity: 0.6, transition: 'opacity 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '0.6'}
                >
                  <RotateCcw size={12} />
                </button>
              </div>
              <iframe
                ref={iframeRef}
                data-testid="live-preview-iframe"
                srcDoc={safeSrcDoc}
                title="Live Preview"
                sandbox="allow-scripts allow-same-origin"
                style={{
                  flex: 1, width: '100%', border: 'none',
                  background: '#fff', minHeight: 0,
                }}
              />
            </div>
            )}
          </div>
        )}

        {viewMode === 'code' && (
          fileSystemTree ? (
            // VS Code-style file explorer when a multi-file project is parsed
            <FileTreeExplorer tree={fileSystemTree} />
          ) : (
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {blocks.map((block) => (
                <div key={block.id} style={{
                  borderRadius: 0, overflow: 'hidden',
                  border: '1px solid var(--border-color)',
                }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '7px 14px',
                  background: 'rgba(124,58,237,0.05)',
                  borderBottom: '1px solid var(--border-color)',
                }}>
                  <span style={{
                    fontSize: 11, fontWeight: 600, color: '#7C3AED',
                    textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}>
                    {block.language || 'code'}
                  </span>
                  <button
                    onClick={() => navigator.clipboard.writeText(block.content)}
                    style={{
                      background: 'rgba(124,58,237,0.1)', border: 'none',
                      color: '#7C3AED', fontSize: 11, cursor: 'pointer',
                      padding: '3px 10px', borderRadius: 0, fontFamily: 'inherit', fontWeight: 500,
                    }}
                  >
                    Copy
                  </button>
                </div>
                <pre style={{
                  margin: 0, padding: 14, background: '#1e1e2e',
                  overflowX: 'auto', fontSize: 12, lineHeight: 1.55,
                }}>
                  <code style={{
                    fontFamily: "'JetBrains Mono', 'Fira Code', monospace", color: '#e6edf3',
                  }}>
                    {block.content}
                  </code>
                </pre>
              </div>
            ))}
            </div>
          )
        )}

        {viewMode === 'dashboard' && (
          <div style={{ flex: 1, display: 'flex', minHeight: 0, margin: '-14px -18px' }}>
            <DashboardShell chatId={chatId} />
          </div>
        )}
      </div>
    </div>
  );
}

// Helpers for iframe document building
function cleanImports(code) {
  return code
    .replace(/^import\s+.*?from\s+['"].*?['"];?\s*$/gm, '')
    .replace(/^import\s+['"].*?['"];?\s*$/gm, '')
    .replace(/^import\s+type\s+.*$/gm, '')
    .replace(/^const\s+\w+\s*=\s*require\s*\(.*?\);?\s*$/gm, '')
    .replace(/^export\s+default\s+/gm, 'const __exported__ = ')
    .replace(/^export\s+/gm, '')
    .replace(/:\s*(React\.FC|FC|JSX\.Element|ReactNode|string|number|boolean|any)\b/g, '')
    .replace(/<(\w+)(Props|Type)>/g, '')
    .replace(/interface\s+\w+\s*\{[^}]*\}/g, '')
    .replace(/type\s+\w+\s*=\s*[^;]+;/g, '')
    .trim();
}

function extractComponentName(code) {
  const funcMatch = code.match(/(?:function|const)\s+([A-Z]\w+)/);
  return funcMatch ? funcMatch[1] : 'App';
}

function escapeHtml(str) {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function MarkdownRenderer({ content, accentColor, modifyMode, onSectionSelect }) {
  const sectionClick = modifyMode && onSectionSelect
    ? (e) => { const txt = e.currentTarget.textContent; if (txt) onSectionSelect(txt.trim()); }
    : undefined;
  const hoverOn = (e) => { e.currentTarget.style.background = `${accentColor}15`; e.currentTarget.style.borderColor = `${accentColor}50`; };
  const hoverOff = (e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; };

  return (
    <div style={{ color: 'var(--text-primary)', fontSize: 14, lineHeight: 1.7 }}>
      <ReactMarkdown
        components={{
          h1: ({ children }) => (
            <div style={{
              fontSize: 20, fontWeight: 700, margin: '20px 0 10px',
              color: 'var(--text-primary)',
              paddingBottom: 8,
              borderBottom: `2px solid ${accentColor}30`,
            }}>{children}</div>
          ),
          h2: ({ children }) => (
            <div
              onClick={sectionClick}
              onMouseEnter={sectionClick ? hoverOn : undefined}
              onMouseLeave={sectionClick ? hoverOff : undefined}
              style={{
                fontSize: 16, fontWeight: 700,
                margin: sectionClick ? '18px -12px 8px' : '18px 0 8px',
                color: accentColor,
                display: 'flex', alignItems: 'center', gap: 8,
                cursor: sectionClick ? 'pointer' : 'default',
                padding: sectionClick ? '8px 12px' : 0,
                border: '1px solid transparent',
                borderRadius: 0,
                transition: 'all 0.15s ease',
              }}
            >
              <ArrowRight size={14} />
              {children}
              {sectionClick && <Pencil size={11} style={{ marginLeft: 'auto', opacity: 0.4 }} />}
            </div>
          ),
          h3: ({ children }) => (
            <div
              onClick={sectionClick}
              onMouseEnter={sectionClick ? hoverOn : undefined}
              onMouseLeave={sectionClick ? hoverOff : undefined}
              style={{
                fontSize: 14, fontWeight: 600,
                margin: sectionClick ? '14px -10px 6px' : '14px 0 6px',
                color: 'var(--text-primary)',
                cursor: sectionClick ? 'pointer' : 'default',
                padding: sectionClick ? '6px 10px' : 0,
                border: '1px solid transparent',
                borderRadius: 0,
                transition: 'all 0.15s ease',
              }}
            >
              {children}
              {sectionClick && <Pencil size={10} style={{ marginLeft: 8, opacity: 0.3, display: 'inline' }} />}
            </div>
          ),
          p: ({ children }) => (
            <p style={{ margin: '7px 0', color: 'var(--text-secondary)', fontSize: 13 }}>{children}</p>
          ),
          ul: ({ children }) => (
            <ul style={{ margin: '6px 0', paddingLeft: 0, listStyle: 'none' }}>{children}</ul>
          ),
          ol: ({ children }) => (
            <ol style={{ margin: '6px 0', paddingLeft: 20, color: 'var(--text-secondary)', fontSize: 13 }}>{children}</ol>
          ),
          li: ({ children }) => (
            <li style={{
              padding: '8px 14px', margin: '4px 0',
              background: 'var(--bg-depth-2)', borderRadius: 0,
              borderLeft: `3px solid ${accentColor}40`,
              color: 'var(--text-secondary)', fontSize: 13,
            }}>{children}</li>
          ),
          strong: ({ children }) => (
            <strong style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{children}</strong>
          ),
          code: ({ node, inline, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const lang = match ? match[1] : '';
            if (!inline && (lang || String(children).includes('\n'))) {
              return (
                <div style={{
                  margin: '10px 0', borderRadius: 0,
                  overflow: 'hidden', border: '1px solid var(--border-color)',
                }}>
                  {lang && (
                    <div style={{
                      padding: '6px 12px', background: 'rgba(0,0,0,0.02)',
                      borderBottom: '1px solid var(--border-color)',
                      fontSize: 10, fontWeight: 600, color: 'var(--text-tertiary)',
                      textTransform: 'uppercase',
                    }}>{lang}</div>
                  )}
                  <pre style={{
                    margin: 0, padding: 14, background: '#1e1e2e',
                    overflowX: 'auto', fontSize: 12, lineHeight: 1.5,
                  }}>
                    <code style={{
                      fontFamily: "'JetBrains Mono', 'Fira Code', monospace", color: '#e6edf3',
                    }}>{String(children).replace(/\n$/, '')}</code>
                  </pre>
                </div>
              );
            }
            return (
              <code style={{
                background: `${accentColor}15`, color: accentColor,
                padding: '2px 6px', borderRadius: 4, fontSize: '0.88em',
              }} {...props}>{children}</code>
            );
          },
          blockquote: ({ children }) => (
            <blockquote style={{
              borderLeft: `3px solid ${accentColor}`,
              padding: '10px 14px', margin: '10px 0',
              background: 'var(--bg-depth-2)', borderRadius: '0 10px 10px 0',
              color: 'var(--text-secondary)',
            }}>{children}</blockquote>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

function StageLoading({ stage, t }) {
  const stageDef = STAGE_DEFS.find(s => s.id === stage);
  const Icon = stageDef?.icon;
  const color = stageDef?.color || '#7C3AED';

  const waitTexts = {
    wireframe: { main: t.waitWireframe, sub: t.waitWireframeSub },
    design: { main: t.waitDesign, sub: t.waitDesignSub },
    code: { main: t.waitCode, sub: t.waitCodeSub },
  };

  const txt = waitTexts[stage] || waitTexts.wireframe;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', height: '100%', minHeight: 300,
      color: 'var(--text-tertiary)', textAlign: 'center',
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: 0,
        background: `${color}15`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 16, animation: 'pulse-glow 2s infinite',
      }}>
        {Icon && <Icon size={22} style={{ color }} />}
      </div>
      <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4 }}>
        {txt.main}
      </p>
      <p style={{ fontSize: 12 }}>
        {txt.sub}
      </p>
    </div>
  );
}

function EmptyState({ t, stages }) {
  return (
    <div data-testid="preview-panel" style={{
      flex: '1 1 62%',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg-depth-1)', padding: 40, overflow: 'hidden',
    }}>
      <div style={{ textAlign: 'center', maxWidth: 500, animation: 'fadeIn 0.6s ease-out' }}>
        <div style={{
          width: 72, height: 72,
          background: 'linear-gradient(135deg, rgba(124,58,237,0.15), rgba(124,58,237,0.08))',
          borderRadius: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 24px',
          border: '1px solid rgba(124,58,237,0.2)',
        }}>
          <Zap size={32} style={{ color: '#7C3AED' }} />
        </div>
        <h2 style={{
          fontSize: 24, fontWeight: 700, color: 'var(--text-primary)',
          marginBottom: 10, letterSpacing: '-0.02em',
        }}>
          {t.pipeline}
        </h2>
        <p style={{
          fontSize: 14, color: 'var(--text-secondary)',
          lineHeight: 1.6, marginBottom: 32,
        }}>
          {t.previewDesc}
        </p>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          {stages.map((stage, i) => (
            <React.Fragment key={stage.id}>
              {i > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'center',
                  color: 'var(--text-tertiary)', opacity: 0.3,
                }}>
                  <ChevronRight size={16} />
                </div>
              )}
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                padding: '16px 20px',
                background: 'var(--bg-depth-2)',
                borderRadius: 0,
                border: '1px solid var(--border-color)',
                minWidth: 110,
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 0,
                  background: `${stage.color}15`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <stage.icon size={17} style={{ color: stage.color }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{stage.label}</span>
                <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{stage.sublabel}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
